import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, type KeySectionResponse, type OutlineSectionResponse } from "../api/client.js";
import type { DocumentTable } from "@mosaic/shared";
import { chooseDocument } from "../lib/documents.js";
import { useShellLayout } from "../lib/shell.js";
import {
  containsAbsolute,
  nextSection,
  previousSection,
  sliceEnd,
  toRelative,
  windowForCitation,
  type AbsoluteRange,
  type LoadedSlice,
} from "../lib/offsets.js";

/**
 * The filing, as text, with a citation resolved to its exact offset.
 *
 * Every fact and every flag links here with the offset it was extracted from, and the
 * reader lands on the sentence itself, so provenance is checkable and the filing is one
 * click away.
 *
 * ## Why it loads one section at a time
 *
 * An information statement runs to hundreds of pages, and scrolling all of it is searching
 * by hand. So the sidebar carries the document's own outline, navigating to a section
 * fetches that section's characters and nothing else, and the whole document stays one
 * click away for a straight read or a find-in-page across everything.
 *
 * ## The offsets
 *
 * Every citation is an **absolute** UTF-16 offset into the document's whole, untrimmed
 * body. A loaded section is a *slice*, and an index into that slice is a different number.
 * Mixing them does not throw and does not look wrong: it renders a real sentence under a
 * citation belonging to a different one. So the slice carries its origin, the origin is
 * always the server's reported `charStart` rather than the one this page asked for, and
 * every conversion goes through `lib/offsets.ts`.
 */

/**
 * Below this a document opens whole; above it, at its first section. It decides the
 * *default* only — every document can be read whole, and every document with an outline
 * can be read a section at a time, whatever its size.
 */
const WHOLE_DOCUMENT_MAX_CHARS = 40_000;

/**
 * Below this, a document's images may be the whole of it and are worth fetching.
 *
 * Deliberately looser than the scan notice's threshold: a scanned exhibit with a caption
 * or a page number per image still has some text and is still nothing but pictures. Above
 * this the text is the document, and a cover logo does not justify a request per view.
 */
const IMAGE_ONLY_MAX_CHARS = 20_000;

/**
 * The reader for one filing at `/filings/:accession`: a document's text, whole or a
 * section at a time, with its contents sidebar and any cited passage highlighted.
 *
 * The query string names the citation (`highlight`, `end`), the document (`doc`,
 * `sequence`), the section (`from`, `to`) and `whole=1` for the whole document.
 */
export function DocumentPage() {
  const { accession = "" } = useParams();
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const goBack = useNavigate();
  /**
   * Absolute offsets, from whatever cited this document. `Number(null ?? "")` is 0, so an
   * absent citation reads as 0 rather than NaN — which is why 0 means "no citation" here
   * and not "the first character".
   */
  const citationStart = Number(params.get("highlight") ?? "");
  const citationEnd = Number(params.get("end") ?? "");
  const sequence = Number(params.get("sequence") ?? "");
  const documentId = params.get("doc");
  const wantsWhole = params.get("whole") === "1";

  const filing = useQuery({ queryKey: ["filing", accession], queryFn: () => api.filing(accession) });

  /**
   * Which document is on screen. The choice lives in `lib/documents.ts` because the shell
   * asks the same question when it scopes Ask to what is being read, and two answers would
   * put a question about one document against another.
   */
  const chosen = useMemo(
    () => chooseDocument(filing.data?.documents, { documentId, sequence }),
    [filing.data, sequence, documentId],
  );

  /**
   * A citation that names a document this filing does not have is a stale link, not
   * a reason to silently show a different document's text at the same offsets.
   */
  const citedDocumentMissing = Boolean(documentId) && chosen?.id !== documentId;
  const hasCitation = !citedDocumentMissing && Number.isInteger(citationStart) && citationStart > 0;

  const outline = useQuery({
    queryKey: ["outline", accession, chosen?.sequence],
    queryFn: () => api.documentOutline(accession, chosen!.sequence),
    enabled: Boolean(chosen),
  });
  const sections = useMemo(() => outline.data?.sections ?? [], [outline.data]);
  /**
   * A tolerant parser is allowed to find nothing, and an outline request is allowed to
   * fail; neither may stop the document being readable. Either way the answer is settled
   * and the text can be asked for. Waiting until it is settled stops this page fetching
   * the whole document and then immediately fetching one section of it.
   */
  const outlineSettled = outline.isSuccess || outline.isError;

  const readingWindow = useMemo<AbsoluteRange | null>(() => {
    if (!outlineSettled || wantsWhole || sections.length === 0) return null;

    const from = Number(params.get("from"));
    const to = Number(params.get("to"));
    if (
      params.get("from") !== null &&
      Number.isInteger(from) &&
      Number.isInteger(to) &&
      to > from
    ) {
      return { charStart: from, charEnd: to };
    }

    // Arriving from a citation: load the section that contains it, so the passage is read
    // under its own heading rather than as a neighbourhood with no stated subject.
    if (hasCitation) return windowForCitation(sections, citationStart, citationEnd);

    if ((outline.data?.charCount ?? 0) <= WHOLE_DOCUMENT_MAX_CHARS) return null;
    const first = sections[0]!;
    return { charStart: first.charStart, charEnd: first.charEnd };
  }, [
    outlineSettled,
    wantsWhole,
    sections,
    params,
    hasCitation,
    citationStart,
    citationEnd,
    outline.data,
  ]);

  const doc = useQuery({
    queryKey: [
      "document",
      accession,
      chosen?.sequence,
      readingWindow?.charStart ?? null,
      readingWindow?.charEnd ?? null,
    ],
    queryFn: () => api.document(accession, chosen!.sequence, readingWindow ?? undefined),
    enabled: Boolean(chosen) && outlineSettled,
    // Continuing into the next section extends the window, which is a new key. Keeping the
    // previous text on screen means the reader's place does not blank out mid-sentence.
    placeholderData: keepPreviousData,
  });

  /**
   * The origin comes off the response, never off `readingWindow`. Offsets are clamped
   * rather than rejected, so a stale `from` past the end of a re-extracted document is
   * answered with the whole document at `charStart: 0`, and a page that assumed it had
   * received what it asked for would misplace every index it renders.
   */
  const slice: LoadedSlice | null = doc.data
    ? { absoluteStart: doc.data.charStart, text: doc.data.text }
    : null;

  const scrollerRef = useRef<HTMLDivElement>(null);
  const markRef = useRef<HTMLElement>(null);
  /**
   * Whether the contents is a sidebar beside the text or a sheet over it, following the
   * shell's own master-detail move one level up.
   *
   * It asks the shell rather than restating the breakpoint: two copies of a number do not
   * fail together, and nothing throws when they drift apart.
   */
  const narrow = useShellLayout() === "phone";
  /**
   * Open by default on a wide screen, closed on a narrow one, where an open contents
   * pushes the first line of the filing far down the screen.
   *
   * Read from the viewport rather than stored, because it is a fact about the screen and
   * not a preference about the document.
   */
  const [contentsOpen, setContentsOpen] = useState(!narrow);
  const [scrolledSection, setScrolledSection] = useState<number | null>(null);

  const navigate = useCallback(
    (range: AbsoluteRange | null) => {
      const next = new URLSearchParams(params);
      if (range) {
        next.set("from", String(range.charStart));
        next.set("to", String(range.charEnd));
        next.delete("whole");
      } else {
        next.delete("from");
        next.delete("to");
        next.set("whole", "1");
      }
      /**
       * `replace`, not push. The loaded window is view state, not a destination: pushing
       * would make Back walk section by section through everything the reader clicked
       * before it left the page. The URL still updates, so a section is still linkable.
       */
      setParams(next, { replace: true });
      // As a sheet it covers what it just navigated to, so it closes itself. As a sidebar
      // it does not: a contents you have to reopen after every click is the same tax twice.
      if (narrow) setContentsOpen(false);
    },
    [params, setParams, narrow],
  );

  const windowStart = readingWindow?.charStart ?? 0;
  // Reset to the top when the reader moves to a different section, but not when the
  // window is merely extended forward — that is the same read continuing.
  useEffect(() => {
    if (scrollerRef.current) scrollerRef.current.scrollTop = 0;
    setScrolledSection(null);
  }, [windowStart, chosen?.sequence]);

  useEffect(() => {
    markRef.current?.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [doc.data, citationStart]);

  /**
   * The images this document references, asked for only when the document is short
   * enough that pictures could be the substance of it. `charCount` decides rather than a
   * warning, because a short document is not necessarily an unreadable one.
   */
  const imagesWorthAsking = (chosen?.charCount ?? 0) < IMAGE_ONLY_MAX_CHARS;
  const images = useQuery({
    queryKey: ["images", accession, chosen?.sequence],
    queryFn: () => api.documentImages(accession, chosen!.sequence),
    enabled: Boolean(chosen) && imagesWorthAsking,
  });

  /**
   * The grids for the span actually being read.
   *
   * Keyed on the served range rather than the requested one, for the reason the slice's
   * origin comes off the response.
   *
   * Not gated on anything. A table is not an extra the way an image is — it is the
   * document, and a document with no table answers with none.
   */
  const tables = useQuery({
    queryKey: ["tables", accession, chosen?.sequence, doc.data?.charStart, doc.data?.charEnd],
    queryFn: () =>
      api.documentTables(accession, chosen!.sequence, {
        charStart: doc.data!.charStart,
        charEnd: doc.data!.charEnd,
      }),
    enabled: Boolean(chosen) && Boolean(doc.data),
    placeholderData: keepPreviousData,
  });

  /**
   * Only when the response says `current`. `stale` and `not_built` are withheld rather
   * than approximated, and the reader then shows the plain text — always correct, merely
   * less legible. A grid placed by offsets the extractor has since moved would render a
   * real table from a real filing over the wrong prose, and nothing would throw.
   */
  const grids = useMemo(
    () => (tables.data?.state === "current" ? tables.data.tables : []),
    [tables.data],
  );

  const blocks = useMemo(
    () => splitIntoBlocks(slice, sections, grids),
    [slice, sections, grids],
  );

  /**
   * Which section the reader is actually in, as opposed to which one was loaded. They are
   * the same until the window is extended or the whole document is shown, and after that
   * only the scroll position knows.
   */
  const onScroll = useCallback(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const line = scroller.getBoundingClientRect().top + 96;
    let current: number | null = null;
    for (const block of blocks) {
      const el = scroller.querySelector<HTMLElement>(`[data-block="${block.charStart}"]`);
      if (el && el.getBoundingClientRect().top <= line) current = block.sectionStart;
    }
    setScrolledSection(current);
  }, [blocks]);

  const currentSection =
    scrolledSection ?? blocks.find((b) => b.sectionStart !== null)?.sectionStart ?? null;

  if (filing.isLoading) {
    return (
      <div className="read">
        <div className="empty">
          <p>Loading…</p>
        </div>
      </div>
    );
  }
  if (filing.isError || !filing.data) {
    return (
      <div className="read">
        <div className="empty">
          <p>That filing could not be loaded.</p>
          <p className="hint">{(filing.error as Error | undefined)?.message}</p>
        </div>
      </div>
    );
  }

  const f = filing.data;
  const hasOutline = sections.length > 0;
  const windowEnd = slice ? sliceEnd(slice) : 0;
  const next = readingWindow && slice ? nextSection(sections, windowEnd) : null;
  const previous = readingWindow ? previousSection(sections, windowStart) : null;
  const showingWhole = readingWindow === null;

  return (
    <div className="read reading">
      {/**
       * What is on screen, said once and always visible in a sticky header. The article
       * header below is a provenance row rather than a second title.
       */}
      <div className="rtop">
        <div className="rtopin">
          <div className="rnav">
            {/**
             * The way back out goes where the reader came from rather than to a fixed
             * place. React Router stamps `location.key` `"default"` on an entry it did not
             * push, so a deep link falls back to the radar instead of walking the reader
             * out of the app.
             *
             * The filing response carries no situation id, so "back to the situation" is
             * not something this page can name, and inferring one from the company would
             * be a guess.
             */}
            {location.key !== "default" ? (
              <button type="button" className="rbackbtn" onClick={() => goBack(-1)}>
                ← Back
              </button>
            ) : (
              <Link className="rbackbtn" to="/">
                ← Radar
              </Link>
            )}
          </div>

          <div className="rid">
            <span className="ridco">{f.companyName ?? f.cik}</span>
            <span className="ridwhat">
              {f.formType}
              {chosen ? ` · ${chosen.documentType ?? chosen.filename}` : ""}
            </span>
          </div>

          <div className="acts">
            {hasOutline && (
              <button
                type="button"
                className="iconbtn"
                aria-pressed={showingWhole}
                onClick={() => {
                  if (!showingWhole) return navigate(null);
                  // Back to the section the reader was in, not to the top of the filing —
                  // the toggle is a change of view, not a change of place.
                  const back =
                    sections.find((s) => s.charStart === currentSection) ?? sections[0]!;
                  navigate({ charStart: back.charStart, charEnd: back.charEnd });
                }}
              >
                {showingWhole ? "By section" : "Whole document"}
              </button>
            )}
          </div>
        </div>

        {/**
         * The navigator, in the sticky header rather than in the scroller.
         *
         * It stays put while the contents is open, because the contents is opened and
         * closed from it. Being in the header also lets the contents overlay only the
         * text: the identity and the way out are never covered.
         */}
        {hasOutline && (
          <div className="secstrip">
            <button
              type="button"
              className="secstripin"
              onClick={() => setContentsOpen((v) => !v)}
              aria-expanded={contentsOpen}
            >
              <span className="secstriptitle">
                {sections.find((sec) => sec.charStart === currentSection)?.title ??
                  (showingWhole ? "Whole document" : "Contents")}
              </span>
              {/* It is a disclosure, and nothing about a bare line of text said so. */}
              <span className="secstripcaret" aria-hidden="true">
                {contentsOpen ? "▴" : "▾"}
              </span>
            </button>
          </div>
        )}
      </div>

      <div className="rsplit">
        {hasOutline && contentsOpen && outline.data && (
          <Contents
            sections={sections}
            keySections={outline.data.keySections}
            currentSection={currentSection}
            onNavigate={navigate}
            /**
             * Closable at every width, so the desktop can give the sidebar's width back to
             * the text. The strip above reopens it either way.
             */
            onClose={() => setContentsOpen(false)}
          />
        )}

        <div className="rscroll" ref={scrollerRef} onScroll={onScroll}>
          <div className="doc">
            {/**
             * Provenance, not a second title: what the sticky header cannot carry — the
             * accession, the filing date, and the way to the source, which stays one click
             * away.
             */}
            <div className="docprov">
              <span>filed {f.filingDate}</span>
              <span>{f.accession}</span>
              {chosen && <span>{chosen.description ?? chosen.filename}</span>}
              <a
                href={`https://www.sec.gov/Archives/edgar/data/${Number(f.cik)}/${f.accession.replace(/-/g, "")}/`}
                target="_blank"
                rel="noreferrer"
              >
                On EDGAR ↗
              </a>
            </div>

            {/**
             * Which document of the filing is on screen, and the way to any other. The route
             * names a filing and the page picks a document, so without this the others are
             * reachable only through a citation. The list is already fetched.
             */}
            {filing.data.documents.length > 1 && (
              <label className="docpick">
                <span className="k">Document</span>
                <select
                  value={chosen?.sequence ?? ""}
                  onChange={(e) => {
                    const next = new URLSearchParams(params);
                    next.set("sequence", e.target.value);
                    // A new document means the old window's offsets belong to nothing.
                    next.delete("from");
                    next.delete("to");
                    next.delete("doc");
                    next.delete("highlight");
                    next.delete("end");
                    setParams(next, { replace: true });
                  }}
                >
                  {filing.data.documents.map((d) => (
                    <option key={d.sequence} value={d.sequence}>
                      {d.sequence} · {d.documentType ?? "document"} · {d.filename}
                      {/* Whether it HAS text is a state worth knowing; how many
                          characters it has is not something anyone navigates by. */}
                      {d.charCount === null ? " — not text" : ""}
                    </option>
                  ))}
                </select>
              </label>
            )}

            {!chosen && (
              <div className="empty">
                <p>No document in this filing has extracted text yet.</p>
                <p className="hint">It may still be in the ingest queue.</p>
              </div>
            )}

            {/**
             * A citation whose offset is outside the document, said rather than ignored:
             * the passage is not in view, and the reader is told so. A stale link after a
             * re-extraction is the ordinary way to get here.
             */}
            {hasCitation && !citedDocumentMissing && doc.data && citationStart >= doc.data.charCount && (
              <div className="notice">
                <b>That citation is past the end of this document</b>
                It points at character {citationStart.toLocaleString()} and the document is{" "}
                {doc.data.charCount.toLocaleString()} characters, so nothing is highlighted.
                The document has most likely been re-extracted since the link was made —
                open it on EDGAR to check the passage directly.
              </div>
            )}

            {citedDocumentMissing && (
              <div className="notice">
                <b>Citation points at a document not in this filing</b>
                The offsets below belong to a different document, so nothing is
                highlighted. Open the filing on EDGAR to check the source directly.
              </div>
            )}

            {/**
             * A document that is pictures says so, rather than being described as text
             * with no headings. Telling a reader to search a scan invites them to conclude
             * the filing does not say a thing when nothing here was ever readable.
             */}
            {(images.data?.images.length ?? 0) > 0 && (doc.data?.charCount ?? 0) < 200 && (
              <div className="notice">
                <b>This document is a scan</b>
                It carries {images.data!.images.length} image(s) and{" "}
                {doc.data?.charCount ?? 0} characters of text, so nothing after the fetch
                tier can read it — not the Screen, not the semantic index, not an
                extraction. The pages are below; read them yourself.
              </div>
            )}

            {/**
             * A document with no recognisable headings is an ordinary outcome, and it
             * reads whole with no sidebar. Saying so distinguishes a parser that found
             * nothing from one that was skipped.
             */}
            {outline.isSuccess &&
              !hasOutline &&
              (doc.data?.charCount ?? 0) > 0 &&
              (images.data?.images.length ?? 0) === 0 && (
              <div className="notice">
                <b>No headings found in this document</b>
                {outline.data?.warnings[0] ??
                  "It has no capitalised headings and no locatable table of contents, so there is nothing to build a contents list from."}{" "}
                The whole document is below; use the browser's find to search it.
              </div>
            )}

            {doc.isError && (
              <div className="notice">
                <b>That document could not be loaded</b>
                {(doc.error as Error | undefined)?.message ?? "The request failed."}
              </div>
            )}

            {doc.isLoading && <p className="deck">Loading document…</p>}

            {slice && previous && (
              <button type="button" className="secnav up" onClick={() => navigate(previous)}>
                ↑ {previous.title}
              </button>
            )}

            {/**
             * The pictures, where the document is pictures.
             *
             * Rendered above the text rather than woven into it: the extractor emits
             * nothing for an `<img>`, so there is no offset at which one sat and any
             * position inside the text would be invented. For a scanned exhibit the images
             * in document order are the document.
             */}
            {(images.data?.images.length ?? 0) > 0 && (
              <div className="docimages">
                {images.data!.images.map((img, i) => (
                  <figure key={`${img.src}:${i}`} className="docimage">
                    <img
                      src={`/api/filings/${accession}/images/${encodeURIComponent(img.src)}`}
                      alt={img.alt ?? ""}
                      /*
                        The filer's own dimensions, which tell a logo from a scanned page.
                        Declared small: leave it small. Declared nothing: it is a scan and
                        wants the column.
                      */
                      width={img.width ?? undefined}
                      height={img.height ?? undefined}
                      className={img.width && img.width < 320 ? "small" : "full"}
                      loading="lazy"
                    />
                    {img.alt && <figcaption>{img.alt}</figcaption>}
                  </figure>
                ))}
              </div>
            )}

            {slice && (
              <Body
                slice={slice}
                blocks={blocks}
                citationStart={hasCitation ? citationStart : null}
                citationEnd={citationEnd}
                markRef={markRef}
              />
            )}

            {/**
             * The boundary. A reader who reaches the end of a section can carry on from
             * here; it extends the window forward instead of replacing it, so the section
             * just read stays above and their place is not lost.
             */}
            {slice && next && (
              <button
                type="button"
                className="secnav down"
                onClick={() => navigate({ charStart: windowStart, charEnd: next.charEnd })}
              >
                ↓ Continue into {next.title}
              </button>
            )}

            {/* Measured against the document's own length, not against "there is no next
                section" — a window the server clamped can run out of sections without
                having reached the end, and saying so would be a claim about the filing. */}
            {slice && !showingWhole && doc.data && windowEnd >= doc.data.charCount && (
              <p className="secend">End of the document.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

interface Block {
  /** Absolute offsets, clipped to what the loaded slice actually holds. */
  charStart: number;
  charEnd: number;
  /** The outline section this block belongs to, or null for text before the first one. */
  sectionStart: number | null;
  /**
   * The grid standing for exactly these characters, when they are a table. The text is
   * still what the offsets name; the grid is a way of showing it, so the block keeps its
   * range and the plain-text fallback needs nothing.
   */
  table: DocumentTable | null;
}

/**
 * The loaded slice, cut at the section boundaries it spans.
 *
 * Blocks exist for two reasons and neither is presentation: they are what a scroll
 * position is resolved against to say which section is being read, and they are what
 * keeps a highlight correct when a quote straddles a boundary — each block marks its own
 * portion rather than one span guessing which side it belongs to.
 *
 * Section headings are not rendered separately. A section starts *at* its own heading, so
 * the text already contains it; printing it again would show every heading twice.
 */
function splitIntoBlocks(
  slice: LoadedSlice | null,
  sections: OutlineSectionResponse[],
  tables: DocumentTable[],
): Block[] {
  if (!slice) return [];
  const start = slice.absoluteStart;
  const end = sliceEnd(slice);

  const grids = tables
    .filter((t) => t.charEnd > start && t.charStart < end)
    .map((t) => ({
      table: t,
      charStart: Math.max(t.charStart, start),
      charEnd: Math.min(t.charEnd, end),
    }))
    .sort((a, b) => a.charStart - b.charStart);

  /**
   * Cut points. Table boundaries always; a section boundary only where it does not fall
   * INSIDE a table.
   *
   * A table that straddles a section boundary would otherwise be cut in two, and each
   * half would carry the same grid, showing the table twice. Losing the heading's exact
   * position inside a table costs nothing; a filer does not start a section halfway down
   * a table.
   */
  const insideTable = (at: number) => grids.some((g) => at > g.charStart && at < g.charEnd);
  const cuts = new Set<number>([start, end]);
  for (const g of grids) {
    cuts.add(g.charStart);
    cuts.add(g.charEnd);
  }
  for (const section of sections) {
    for (const at of [section.charStart, section.charEnd]) {
      if (at > start && at < end && !insideTable(at)) cuts.add(at);
    }
  }

  const points = [...cuts].sort((a, b) => a - b);
  const blocks: Block[] = [];
  for (let i = 0; i + 1 < points.length; i += 1) {
    const from = points[i]!;
    const to = points[i + 1]!;
    if (to <= from) continue;
    /**
     * The innermost section containing this segment's start. Contiguous segments rather
     * than one block per section, so text in a gap between two sections is rendered
     * rather than dropped.
     */
    const section = sections.filter((sn) => sn.charStart <= from && sn.charEnd > from).at(-1);
    blocks.push({
      charStart: from,
      charEnd: to,
      sectionStart: section?.charStart ?? null,
      table: grids.find((g) => g.charStart === from && g.charEnd === to)?.table ?? null,
    });
  }
  return blocks;
}

/**
 * Renders the loaded slice with the cited passage marked.
 *
 * The citation is absolute and the text is a slice, so every offset crosses exactly one
 * boundary — through `toRelative`, which answers `null` rather than a clamped number when
 * the passage is not in this slice at all. Clamping would place the mark at the edge of
 * whatever happened to be loaded and present it as the source.
 */
function Body({
  slice,
  blocks,
  citationStart,
  citationEnd,
  markRef,
}: {
  slice: LoadedSlice;
  blocks: Block[];
  citationStart: number | null;
  citationEnd: number;
  markRef: React.RefObject<HTMLElement | null>;
}) {
  /**
   * `end` is the quote's located end. Falling back to a sentence's worth keeps a link
   * that carries only a start from highlighting nothing.
   */
  const mark =
    citationStart !== null && containsAbsolute(slice, citationStart)
      ? {
          charStart: citationStart,
          charEnd:
            Number.isInteger(citationEnd) && citationEnd > citationStart
              ? citationEnd
              : citationStart + 320,
        }
      : null;

  let markUsed = false;
  return (
    <div className="doctext">
      {blocks.map((block) => {
        const blockStart = toRelative(slice, block.charStart);
        const blockEnd = toRelative(slice, block.charEnd);
        if (blockStart === null || blockEnd === null) return null;
        const text = slice.text.slice(blockStart, blockEnd);

        // Intersect in absolute offsets, then subtract the block's origin exactly once.
        const from = mark ? Math.max(mark.charStart, block.charStart) : 0;
        const to = mark ? Math.min(mark.charEnd, block.charEnd) : 0;
        const marked = Boolean(mark) && to > from;
        const attach = marked && !markUsed;
        if (attach) markUsed = true;

        return (
          <div
            key={block.charStart}
            className={block.table ? "docblock docblock-table" : "docblock"}
            data-block={block.charStart}
            data-section={block.sectionStart ?? undefined}
          >
            {block.table ? (
              /**
               * A citation landing inside a table marks the whole table, not a cell.
               * The grid holds each cell's text verbatim but not its offsets, and
               * re-locating the cell text here would be a second answer to a question
               * the extractor already answered, disagreeing with it where a filer
               * repeats a figure.
               */
              <TableBlock
                table={block.table}
                text={text}
                marked={marked}
                markRef={attach ? markRef : undefined}
              />
            ) : marked ? (
              <>
                {text.slice(0, from - block.charStart)}
                <mark ref={attach ? markRef : undefined}>
                  {text.slice(from - block.charStart, to - block.charStart)}
                </mark>
                {text.slice(to - block.charStart)}
              </>
            ) : (
              text
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * A table, as a grid whose columns stay under their own headings. In the flat text a row
 * reads as "Total assets 201,792 24,653", and which column is which is not recoverable.
 *
 * **The plain text is one press away and is never thrown out.** It is what the offsets
 * name, what a browser's find-in-page searches, and what the reader can check the grid
 * against.
 *
 * The grid scrolls horizontally with the label column pinned, and a wide one opens full
 * screen. Not card-per-row: stacking each row into a card destroys the columnar
 * comparison that is the reason to read the table.
 */
function TableBlock({
  table,
  text,
  marked,
  markRef,
}: {
  table: DocumentTable;
  text: string;
  marked: boolean;
  markRef?: React.RefObject<HTMLElement | null>;
}) {
  const [plain, setPlain] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const columns = table.rows[0]?.length ?? 0;

  return (
    <figure
      className={marked ? "doctable is-marked" : "doctable"}
      ref={markRef as React.RefObject<HTMLElement> | undefined}
    >
      <figcaption className="doctable-bar">
        <span className="doctable-what">
          {table.rows.length} × {columns}
          {table.kind === "figures" ? " figures" : ""}
        </span>
        <span className="doctable-acts">
          {/*
            Only where it buys something. A four-column table is already legible in
            place, and an expand control on every table is a control the reader learns
            to ignore before reaching the one that needed it.
          */}
          {columns > WIDE_TABLE_COLUMNS && !plain ? (
            <button type="button" onClick={() => setExpanded(true)}>
              Expand
            </button>
          ) : null}
          <button type="button" onClick={() => setPlain((p) => !p)}>
            {plain ? "Grid" : "Text"}
          </button>
        </span>
      </figcaption>

      {plain ? <div className="doctable-plain">{text}</div> : <Grid table={table} />}

      {expanded ? (
        <div className="doctable-full" role="dialog" aria-modal="true" aria-label="Table">
          <div className="doctable-fullbar">
            <span>
              {table.rows.length} × {columns}
            </span>
            <button type="button" onClick={() => setExpanded(false)}>
              Close
            </button>
          </div>
          <Grid table={table} />
        </div>
      ) : null}
    </figure>
  );
}

/** Above this many columns a table offers a full-screen view; below it, it is read in place. */
const WIDE_TABLE_COLUMNS = 8;

/**
 * A number, near enough to right-align. Not a parse: SEC tables carry "1,204", "(37)",
 * "$", "—" and "12.5%" in the same column, and the point is only that a column of
 * figures should line up on the right the way the filing prints it.
 */
function looksNumeric(cell: string): boolean {
  return /\d/.test(cell) && !/[A-Za-z]{3}/.test(cell);
}

/**
 * An extracted table's cells, with the first column pinned and numeric cells
 * right-aligned.
 *
 * @internal
 */
function Grid({ table }: { table: DocumentTable }) {
  return (
    <div className="doctable-scroll">
      <table>
        <tbody>
          {table.rows.map((row, r) => (
            <tr key={r}>
              {row.map((cell, c) => (
                <td
                  key={c}
                  /*
                   * The first column is pinned: in SEC tables it is the line item, and a
                   * figure scrolled away from its label is unreadable. It is a bet about
                   * layout rather than a claim about meaning.
                   */
                  className={
                    (c === 0 ? "is-label" : "") + (looksNumeric(cell) ? " is-num" : "")
                  }
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface Group {
  head: OutlineSectionResponse | null;
  children: OutlineSectionResponse[];
}

/**
 * Level-2 headings folded under the level-1 section that precedes them.
 *
 * A document with no level-1 sections at all comes back as one flat group rather than an
 * empty tree.
 */
function groupSections(sections: OutlineSectionResponse[]): Group[] {
  const groups: Group[] = [];
  for (const section of sections) {
    if (section.level === 1 || groups.length === 0) {
      groups.push({ head: section.level === 1 ? section : null, children: [] });
      if (section.level === 1) continue;
    }
    groups[groups.length - 1]!.children.push(section);
  }
  return groups;
}

/**
 * The document's contents sidebar: the recognised key sections, then the outline with
 * level-2 headings folded under their level-1 section.
 *
 * @internal
 * @param currentSection The `charStart` of the section being read, or null.
 * @param onNavigate Called with the absolute range of the section chosen.
 * @param onClose Present when the sidebar can be closed; its header becomes the control.
 */
function Contents({
  sections,
  keySections,
  currentSection,
  onNavigate,
  onClose,
}: {
  sections: OutlineSectionResponse[];
  keySections: KeySectionResponse[];
  currentSection: number | null;
  onNavigate: (range: AbsoluteRange | null) => void;
  onClose?: () => void;
}) {
  const groups = useMemo(() => groupSections(sections), [sections]);
  /**
   * Only the groups the reader has actually clicked. Everything else follows where they
   * are reading — an override map rather than state synced by an effect, so the open set
   * can never drift out of step with the current section.
   */
  const [overrides, setOverrides] = useState<Map<number, boolean>>(new Map());

  return (
    <aside className="toc" aria-label="Document contents">
      {/**
       * The whole header closes it, not just the ✕: the glyph is the smallest target on
       * the panel. The ✕ stays as the affordance that says the bar is a control.
       */}
      {onClose ? (
        <button type="button" className="tochd tochdbtn" onClick={onClose}>
          <span>Contents</span>
          <span className="tocclose" aria-hidden="true">
            ✕
          </span>
        </button>
      ) : (
        <div className="tochd">
          <span>Contents</span>
        </div>
      )}

      <div className="tocgroup">
        <div className="toclabel">Key sections</div>
        {keySections.length === 0 ? (
          <p className="tocnote">
            No separation sections recognised by name. The full outline below is the map.
          </p>
        ) : (
          keySections.map((k) => (
            <button
              key={k.kind}
              type="button"
              className="tocquick"
              title={`${k.why} — filed as “${k.title}”`}
              aria-current={currentSection === k.charStart ? "true" : undefined}
              onClick={() => onNavigate({ charStart: k.charStart, charEnd: k.charEnd })}
            >
              <b>{k.label}</b>
            </button>
          ))
        )}
      </div>

      {/**
       * No "whole document" entry here: the header carries that control, it is a change of
       * view rather than a place in the document, and the outline is the document's own
       * structure and nothing else.
       */}
      <div className="tocgroup">
        <div className="toclabel">Outline</div>
        {groups.map((group, i) => {
          const key = group.head?.charStart ?? -1 - i;
          const holdsCurrent =
            currentSection !== null &&
            (group.head?.charStart === currentSection ||
              group.children.some((c) => c.charStart === currentSection));
          const open = overrides.get(key) ?? holdsCurrent;
          return (
            <div key={key} className="tocsec">
              {group.head && (
                <div className="tocrow">
                  {group.children.length > 0 && (
                    <button
                      type="button"
                      className="toctwist"
                      aria-expanded={open}
                      aria-label={`${open ? "Collapse" : "Expand"} ${group.head.title}`}
                      onClick={() =>
                        setOverrides((m) => new Map(m).set(key, !open))
                      }
                    >
                      {open ? "▾" : "▸"}
                    </button>
                  )}
                  <button
                    type="button"
                    className={`tocitem${group.children.length === 0 ? " nokids" : ""}`}
                    aria-current={currentSection === group.head.charStart ? "true" : undefined}
                    onClick={() =>
                      onNavigate({
                        charStart: group.head!.charStart,
                        charEnd: group.head!.charEnd,
                      })
                    }
                  >
                    {group.head.title}
                  </button>
                </div>
              )}
              {(open || !group.head) &&
                group.children.map((child) => (
                  <button
                    key={child.charStart}
                    type="button"
                    className={`tocitem sub${group.head ? "" : " flat"}`}
                    aria-current={currentSection === child.charStart ? "true" : undefined}
                    onClick={() =>
                      onNavigate({ charStart: child.charStart, charEnd: child.charEnd })
                    }
                  >
                    {child.title}
                  </button>
                ))}
            </div>
          );
        })}
      </div>
    </aside>
  );
}
